import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
// Public client — no credentials (public self-service).
const pub = axios.create({ baseURL: `${BASE}/api/smartinspect/roi` });
// Admin/consultant client — cookie session.
const adm = axios.create({ baseURL: `${BASE}/api/admin/smartinspect`, withCredentials: true });

// ---- Public (no login) ----
export const siPublicConfig = () => pub.get("/config").then((r) => r.data);
export const siPublicCreate = (answers) => pub.post("/assessments", { answers }).then((r) => r.data);
export const siPublicGet = (id, token) => pub.get(`/assessments/${id}`, { params: { token } }).then((r) => r.data);
export const siPublicUpdate = (id, resume_token, answers, current_step) =>
  pub.patch(`/assessments/${id}`, { resume_token, answers, current_step }).then((r) => r.data);
export const siPublicComplete = (id, resume_token, answers) =>
  pub.post(`/assessments/${id}/complete`, { resume_token, answers }).then((r) => r.data);
export const siPublicCalculate = (id, resume_token, answers) =>
  pub.post(`/assessments/${id}/calculate`, { resume_token, answers }).then((r) => r.data);

// ---- Admin / Consultant ----
export const siMeta = () => adm.get("/meta").then((r) => r.data);
export const siInstructionsData = () => adm.get("/instructions-data").then((r) => r.data);
export const siListQuestions = () => adm.get("/roi-questions").then((r) => r.data);
export const siCreateQuestion = (d) => adm.post("/roi-questions", d).then((r) => r.data);
export const siUpdateQuestion = (id, d) => adm.put(`/roi-questions/${id}`, d).then((r) => r.data);
export const siDeleteQuestion = (id) => adm.delete(`/roi-questions/${id}`).then((r) => r.data);
export const siReorderQuestions = (items) => adm.post("/roi-questions/reorder", { items }).then((r) => r.data);
export const siSeedDefaults = () => adm.post("/roi-questions/seed-defaults").then((r) => r.data);

export const siListAssessments = () => adm.get("/roi-assessments").then((r) => r.data);
export const siArchiveAssessment = (id, payload) => adm.post(`/roi-assessments/${id}/archive`, payload).then((r) => r.data);
export const siRestoreAssessment = (id) => adm.post(`/roi-assessments/${id}/restore`).then((r) => r.data);
export const siDeleteAssessment = (id) => adm.delete(`/roi-assessments/${id}`).then((r) => r.data);
export const siCreateAssessment = (mode, answers) => adm.post("/roi-assessments", { mode, answers }).then((r) => r.data);
export const siGetAssessment = (id) => adm.get(`/roi-assessments/${id}`).then((r) => r.data);
export const siUpdateAssessment = (id, body) => adm.patch(`/roi-assessments/${id}`, body).then((r) => r.data);
export const siCalculateAssessment = (id, body = {}) => adm.post(`/roi-assessments/${id}/calculate`, body).then((r) => r.data);
export const siFinalizeReport = (id) => adm.post(`/roi-assessments/${id}/finalize`).then((r) => r.data);
export const siReportPdfUrl = (id) => `${BASE}/api/admin/smartinspect/roi-assessments/${id}/report.pdf`;
export const siListVersions = (id) => adm.get(`/roi-assessments/${id}/versions`).then((r) => r.data);
export const siGetVersion = (id, vid) => adm.get(`/roi-assessments/${id}/versions/${vid}`).then((r) => r.data);
export const siCloneVersion = (id, vid) => adm.post(`/roi-assessments/${id}/versions/${vid}/clone`).then((r) => r.data);
export const siVersionPdfUrl = (id, vid) => `${BASE}/api/admin/smartinspect/roi-assessments/${id}/versions/${vid}/report.pdf`;
export const siEmailVersionReport = (id, vid, body) => adm.post(`/roi-assessments/${id}/versions/${vid}/email-report`, body).then((r) => r.data);
export const siGetClientLogo = (id) => adm.get(`/roi-assessments/${id}/logo`).then((r) => r.data);
export const siUploadClientLogo = (id, file) => { const fd = new FormData(); fd.append("logo", file); return adm.post(`/roi-assessments/${id}/logo`, fd).then((r) => r.data); };
export const siDeleteClientLogo = (id) => adm.delete(`/roi-assessments/${id}/logo`).then((r) => r.data);
export const siDemoSample = () => adm.get("/roi-demo-sample").then((r) => r.data);

// ---- ROI Formula Mapping (config layer only; stored, never executed) ----
export const siListFormulas = () => adm.get("/roi-formulas").then((r) => r.data);
export const siCreateFormula = (d) => adm.post("/roi-formulas", d).then((r) => r.data);
export const siUpdateFormula = (id, d) => adm.put(`/roi-formulas/${id}`, d).then((r) => r.data);
export const siDeleteFormula = (id) => adm.delete(`/roi-formulas/${id}`).then((r) => r.data);
export const siReorderFormulas = (items) => adm.post("/roi-formulas/reorder", { items }).then((r) => r.data);

// ---- Commercial Packages (Sprint 5B) ----
export const siListPackages = () => adm.get("/packages").then((r) => r.data);
export const siCreatePackage = (d) => adm.post("/packages", d).then((r) => r.data);
export const siUpdatePackage = (id, d) => adm.put(`/packages/${id}`, d).then((r) => r.data);
export const siDeletePackage = (id) => adm.delete(`/packages/${id}`).then((r) => r.data);
export const siReorderPackages = (items) => adm.post("/packages/reorder", { items }).then((r) => r.data);
export const siPublicPackages = () => pub.get("/packages").then((r) => r.data);

// ---- Global Add-ons Master (Sprint 8X) ----
export const siListAddons = () => adm.get("/addons").then((r) => r.data);
export const siCreateAddon = (d) => adm.post("/addons", d).then((r) => r.data);
export const siUpdateAddon = (id, d) => adm.put(`/addons/${id}`, d).then((r) => r.data);
export const siDeleteAddon = (id) => adm.delete(`/addons/${id}`).then((r) => r.data);
export const siReorderAddons = (items) => adm.post("/addons/reorder", { items }).then((r) => r.data);
export const siPublicAddons = () => pub.get("/addons").then((r) => r.data);

// Sprint 8Y — display-only "cost per inspection" helper text. Divides the
// regional monthly package price by the monthly inspection limit and formats
// with the region's currency (2 decimals). Returns "Custom pricing" for
// unlimited / custom / limit-less / price-less packages (e.g. Enterprise Plus).
export function costPerInspectionText({ price, monthlyLimit, unlimited, custom, currencyCode }) {
  const p = Number(price);
  const lim = Number(monthlyLimit);
  if (custom || unlimited || !lim || lim <= 0 || price === null || price === undefined || price === "" || isNaN(p)) {
    return "Custom pricing";
  }
  const per = p / lim;
  let money;
  try {
    money = new Intl.NumberFormat("en-US", { style: "currency", currency: currencyCode || "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(per);
  } catch { money = `${currencyCode || ""} ${per.toFixed(2)}`; }
  return `≈ ${money} per inspection`;
}

// Sprint 8P — resolve a regional price with USA/base fallback + missing flag.
export function resolveRegionalPrice(regional, region, base) {
  const rp = regional || {};
  const has = (k) => k in rp && rp[k] !== null && rp[k] !== "" && rp[k] !== undefined;
  if (region && has(region)) return { amount: rp[region], missing: false };
  const missing = !!region && region !== "USA";
  if (has("USA")) return { amount: rp["USA"], missing };
  return { amount: base ?? null, missing };
}

// Unique active Pricing Regions (with currency) for the admin regional pricing
// tables — derived from the Countries & Currencies config.
export function activePricingRegions(geoRecords) {
  const seen = {};
  (geoRecords || [])
    .filter((g) => g.active && (g.pricing_region || "").trim())
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
    .forEach((g) => {
      const r = g.pricing_region;
      if (!seen[r]) seen[r] = { region: r, currency_code: g.currency_code, currency_symbol: g.currency_symbol };
    });
  return Object.values(seen);
}

// ---- Country / Currency / Pricing Region reference (Sprint 8O) ----
export const siPublicGeo = () => pub.get("/country-currency-regions").then((r) => r.data);
export const siListGeo = () => adm.get("/country-currency-regions").then((r) => r.data);
export const siCreateGeo = (d) => adm.post("/country-currency-regions", d).then((r) => r.data);
export const siUpdateGeo = (id, d) => adm.put(`/country-currency-regions/${id}`, d).then((r) => r.data);
export const siDeleteGeo = (id) => adm.delete(`/country-currency-regions/${id}`).then((r) => r.data);
export const siReorderGeo = (items) => adm.post("/country-currency-regions/reorder", { items }).then((r) => r.data);
export const siSeedGeo = () => adm.post("/country-currency-regions/seed-defaults").then((r) => r.data);

// ---- Regional Device Cost (Sprint 8Q) ----
export const siListRegionDeviceCosts = () => adm.get("/region-device-costs").then((r) => r.data);
export const siSaveRegionDeviceCosts = (rows) => adm.put("/region-device-costs", { rows }).then((r) => r.data);

// When a Country is selected, derive the linked currency + pricing region from
// the country question's admin-configured option data. Returns a patch to merge
// into the assessment answers. Preferred Currency defaults from the country but
// remains user-overridable. Pricing Region is stored but never shown publicly.
export function countryCascadePatch(countryQuestion, countryValue) {
  const rows = (countryQuestion && countryQuestion.options_data) || [];
  const rec = rows.find((r) => (r.value ?? r.country_name) === countryValue);
  if (!rec) return { country: countryValue };
  return {
    country: rec.country_name || countryValue,
    country_code: rec.country_code || "",
    pricing_region: rec.pricing_region || "",
    currency_name: rec.currency_name || "",
    currency_symbol: rec.currency_symbol || "",
    preferred_currency: rec.currency_code || "",
  };
}

// Find the Country question within grouped steps or a flat question list.
export function findCountryQuestion(source) {
  if (!source) return null;
  const flat = Array.isArray(source)
    ? source.flatMap((s) => (s.sections ? s.sections.flatMap((sec) => sec.questions || []) : [s]))
    : [];
  return flat.find((q) => q && q.field_key === "country") || null;
}

// When the user changes Preferred Currency directly, map it to the matching
// Pricing Region so commercial prices follow the selected currency (Sprint 8P).
// Region data comes from the Country question's option data (currency -> region).
export function currencyCascadePatch(countryQuestion, currencyValue) {
  const rows = (countryQuestion && countryQuestion.options_data) || [];
  const rec = rows.find((r) => r.currency_code === currencyValue);
  if (!rec) return { preferred_currency: currencyValue };
  return {
    preferred_currency: currencyValue,
    pricing_region: rec.pricing_region || "",
    currency_name: rec.currency_name || "",
    currency_symbol: rec.currency_symbol || "",
  };
}

// Auto-calculated implementation investment from operating locations, driven by
// the admin-configured Implementation Investment Rules (single source of truth).
// Sprint 8X — resolves the amount for the given Pricing Region (exact → USA →
// flat `investment`). Falls back to legacy tiers only if rules are unavailable.
export function siImplementationInvestment(operatingLocations, rules, region) {
  const n = Number(operatingLocations) || 0;
  const rs = (rules && rules.length) ? [...rules].sort((a, b) => (a.min_locations || 0) - (b.min_locations || 0)) : null;
  if (!rs) {
    if (n <= 1) return 10000;
    if (n <= 5) return 15000;
    if (n <= 15) return 20000;
    return 30000;
  }
  const pick = (r) => {
    const ri = r.regional_investment;
    if (ri && typeof ri === "object") {
      if (region && ri[region] != null && ri[region] !== "") return Number(ri[region]) || 0;
      if (ri.USA != null && ri.USA !== "") return Number(ri.USA) || 0;
    }
    return Number(r.investment) || 0;
  };
  for (const r of rs) {
    const mn = r.min_locations || 0;
    const mx = r.max_locations;
    if (n >= mn && (mx == null || n <= mx)) return pick(r);
  }
  if (n < (rs[0].min_locations || 0)) return pick(rs[0]);
  return pick(rs[rs.length - 1]);
}

export const siGetImplementationRules = () => pub.get(`/implementation-rules`).then((r) => r.data);
export const siAdminGetImplementationRules = () => adm.get(`/implementation-rules`).then((r) => r.data);
export const siSaveImplementationRules = (rules) => adm.put(`/implementation-rules`, { rules }).then((r) => r.data);

// Build the grouped steps config from a flat question list (admin/consultant reuse).
export function groupSteps(questions, { customerOnly = false } = {}) {
  const STEP_TITLES = {
    step1: "Company and Operations",
    step2: "Current Inspection Process",
    step3: "SmartInspect and Investment",
    step4: "ROI Results",
  };
  const steps = ["step1", "step2", "step3", "step4"].map((key, i) => ({ key, index: i + 1, title: STEP_TITLES[key], sections: [] }));
  const byKey = Object.fromEntries(steps.map((s) => [s.key, s]));
  const active = questions
    .filter((q) => q.active && q.visible && (!customerOnly || q.customer_visible))
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  for (const q of active) {
    const s = byKey[q.step];
    if (!s) continue;
    let sec = s.sections.find((x) => x.section === (q.section || "General"));
    if (!sec) { sec = { section: q.section || "General", questions: [] }; s.sections.push(sec); }
    sec.questions.push(q);
  }
  return steps;
}
